import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { filter } from 'lodash';
import { useEffect, useState } from 'react';

// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  TablePagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Box,
} from '@mui/material';
import { CSVLink } from 'react-csv';
import { FileText } from 'lucide-react';

import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import InfoTooltip from '../components/InfoTooltip';
import { UserListHead } from '../sections/@dashboard/user';

import { getMonthlyStatement } from '../helper/backend_helper';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: '', label: 'BnB Name', alignRight: true },
  { id: '', label: 'No. of cleanings', alignRight: true },
  { id: '', label: 'Money Found (Cleaner)', alignRight: true },
  { id: '', label: 'Money Found (Guest)', alignRight: true },
  { id: '', label: 'Money Mismatches', alignRight: true },
  { id: '', label: 'Total Cleaning Cost', alignRight: true },
  { id: '', label: 'Total Bedsheet Cost', alignRight: true },
  { id: '', label: 'Total Expenses', alignRight: true },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const formatCurrency = (value) => Number(value || 0).toFixed(2);

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => String(_user?._id || '').toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export default function CleaningMonthlyStatement() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);

  const [page, setPage] = useState(0);

  const [order, setOrder] = useState('asc');

  const [selected, setSelected] = useState([]);

  const [orderBy, setOrderBy] = useState('name');

  const [filterName, setFilterName] = useState('');

  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [clickedRowData, setClickedRowData] = useState(null);
  const [users, setUsers] = useState([]);

  const currentDate = new Date();
  const [month, setMonth] = useState(String(currentDate.getMonth() + 1)); // getMonth() is 0-based
  const [year, setYear] = useState(String(currentDate.getFullYear()));

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, index) => currentYear - index); // Last 10 years

  const handleMonthChange = (event) => {
    setPage(0);
    setMonth(String(event.target.value));
  };

  const handleYearChange = (event) => {
    setPage(0);
    setYear(String(event.target.value));
  };

  async function fetchData() {
    try {
      setLoading(true);
      const usersResponse = await getMonthlyStatement(month, year);
      setUsers(usersResponse?.data?.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [month, year]);

  const [loading, setLoading] = useState(false);

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - users.length) : 0;

  const filteredUsers = applySortFilter(users, getComparator(order, orderBy), filterName);

  const isNotFound = !filteredUsers.length && !!filterName;
  // const encodedData = encodeURIComponent(JSON.stringify(clickedRow));
  return (
    <>
      <Helmet>
        <title> Monthly Statement </title>
      </Helmet>

      <CSVLink
        data={filteredUsers.map((item) => ({
          CleanerName: item?._id,
          NoOfCleanings: item?.noOfCleanings,
          MoneyFoundCleaner: item?.totalCleanerMoneyFound ?? item?.totalMoneyFound,
          MoneyFoundGuest: item?.totalGuestMoneyFound,
          MoneyMismatches: item?.moneyMismatchCount,
          TotalCleaningCost: item?.totalCleaningCost,
          TotalBedsheetCost: item?.totalBedsheetCost,
          TotalExpenses: item?.totalExpense,
        }))}
        filename={`statement-${month}-${year}.csv`}
        className="flex w-48 mb-2 items-center border border-gray-200 rounded-md px-4 py-2 text-white hover:bg-[#142A47] bg-[#1E3A5F]"
      >
        <FileText size={16} /> &nbsp; Export CSV
      </CSVLink>
      <Card sx={{ p: 3 }}>
        <Stack mb={3} direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4">Monthly Statement{' '}<InfoTooltip width={20} title="A per-cleaner financial summary for the selected month, totalling cleanings done, money found, mismatches, and cleaning, bedsheet and total expense costs. Figures are aggregated automatically from the individual cleaning reports submitted by cleaners. Pick a month and year to view a period, then use Export CSV to download the summary for accounting." /></Typography>
          <Box display="flex" gap={2}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={handleMonthChange}>
                {months.map((m) => (
                  <MenuItem key={m.value} value={String(m.value)}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={handleYearChange}>
                {years.map((y) => (
                  <MenuItem key={y} value={String(y)}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>

        <Scrollbar>
          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={users.length}
                numSelected={selected.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                  return (
                    <TableRow hover key={row?._id}>
                      <TableCell align="left">{row?._id}</TableCell>
                      <TableCell align="left">{row?.noOfCleanings}</TableCell>
                      <TableCell align="left">{formatCurrency(row?.totalCleanerMoneyFound ?? row?.totalMoneyFound)}</TableCell>
                      <TableCell align="left">{formatCurrency(row?.totalGuestMoneyFound)}</TableCell>
                      <TableCell align="left">{row?.moneyMismatchCount || 0}</TableCell>
                      <TableCell align="left">{formatCurrency(row?.totalCleaningCost)}</TableCell>
                      <TableCell align="left">{formatCurrency(row?.totalBedsheetCost)}</TableCell>
                      <TableCell align="left">{formatCurrency(row?.totalExpense)}</TableCell>
                    </TableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={8} />
                  </TableRow>
                )}
                {loading && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={8}>
                      <CircularProgress size="20px" />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

              {isNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                      <Paper
                        sx={{
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="h6" paragraph>
                          Not found
                        </Typography>

                        <Typography variant="body2">
                          No results found for &nbsp;
                          <strong>&quot;{filterName}&quot;</strong>.
                          <br /> Try checking for typos or using complete words.
                        </Typography>
                      </Paper>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          rowsPerPageOptions={[100, 200, 300]}
          component="div"
          count={users.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        {/*     
        <Link
          
          style={{ textDecoration: 'none' }}
          // to={`/dashboard/user-detail/${encodeURIComponent(JSON.stringify(clickedRow))}`}
          to={`/dashboard/user-detail?data=${encodedData}`}
        > */}
        <MenuItem
          onClick={() => navigate('/contractor/contractor-detail', { state: { clickedRowData } })}
          sx={{ color: 'success.main' }}
        >
          <Iconify icon={'eva:image-outline'} sx={{ mr: 2 }} />
          Detail
        </MenuItem>
        {/* </Link> */}
        <MenuItem>
          <Iconify icon={'eva:edit-outline'} sx={{ mr: 2 }} />
          Block
        </MenuItem>

        <MenuItem sx={{ color: 'error.main' }}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>
    </>
  );
}
